import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { components, OptionProps } from 'react-select';
import styled from 'styled-components';
import { Form } from 'components/atoms/form';
import { Input, TextArea } from 'components/atoms/input';
import { Option, Select } from 'components/atoms/select';
import { useTranslation } from 'hooks/use-translation';
import { useAllFieldsQuery } from 'queries';

const LabelText = styled.div`
  font-size: var(--font-size-sm);
  line-height: var(--line-height-caption-01);
  font-weight: 400;
  letter-spacing: 0.32px;
  color: var(--gray-70);
  margin: var(--spacing-05) 0 var(--spacing-03) 0;
`;

const KeyEventsWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const StyledFormItem = styled(Form.Item)`
  width: 14rem;
`;

const FormItemWithPadding = styled(Form.Item)`
  padding-top: 0.25rem;
`;

const OptionComp = (props: OptionProps<Option, false>) => {
  const { data } = props;

  return (
    <span data-cy={`key-event-option.${data.label}`}>
      <components.Option {...props}>
        <span>{data.label}</span>
      </components.Option>
    </span>
  );
};

export const IntervalForm = ({ form, field, isDisabled = false }) => {
  const onFinish = (_: any) => {};

  const onFinishFailed = (_: any) => {};

  const { data: allFields } = useAllFieldsQuery();

  const events = allFields?.filter(
    (fieldItem) =>
      fieldItem.fieldType === 'KEY_EVENTS' &&
      fieldItem?.metaData?.relatedFieldId,
  );

  const getInitialValues = (f) => {
    if (f) {
      const {
        fieldLabel,
        description,
        metaData: { startKeyEventId, endKeyEventId, intervalBenchmark },
      } = f;

      const startKeyEvent = events?.find(
        (event) => event.fieldId === startKeyEventId,
      );

      const endKeyEvent = events?.find(
        (event) => event.fieldId === endKeyEventId,
      );

      return {
        intervalName: fieldLabel,
        intervalDescription: description,
        ...(startKeyEvent?.fieldLabel
          ? {
              startKeyEvent: {
                label: startKeyEvent?.fieldLabel,
                value: startKeyEventId,
              },
            }
          : {}),
        ...(endKeyEvent?.fieldLabel
          ? {
              endKeyEvent: {
                label: endKeyEvent?.fieldLabel,
                value: endKeyEventId,
              },
            }
          : {}),
        benchmark: !!intervalBenchmark,
        benchmarkValue: intervalBenchmark,
      };
    }

    return {
      intervalName: '',
      fieldLabel: '',
      description: '',
      fieldType: 'INTERVALS',
      fieldDataType: 'NUMBER',
    };
  };

  const [intervalData, setIntervalData] = useState({
    startKeyEventId: field.metaData.startKeyEventId,
    endKeyEventId: field.metaData.endKeyEventId,
  });

  const requiredMessage = useTranslation('input.required');
  const placeholderText = useTranslation(
    'interval_form.select_key_event.placeholder',
  );

  return (
    <Form
      form={form}
      name="new_event_form"
      layout="vertical"
      initialValues={getInitialValues(field)}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
    >
      <Form.Item
        name="intervalName"
        label="Name of the Interval"
        rules={[{ required: true, message: requiredMessage }]}
      >
        <Input
          name="intervalName"
          autoFocus
          id="add-interval.name"
          data-cy="add-interval.name"
        />
      </Form.Item>
      <Form.Item name="intervalDescription" label="Description">
        <TextArea
          autoSize={{ minRows: 1, maxRows: 3 }}
          data-cy="add-interval.desc"
        />
      </Form.Item>
      <span style={{ margin: '1rem 0' }}>
        <FormattedMessage id="settings.intervals.form.interval_measure" />
      </span>
      <LabelText>
        <FormattedMessage id="settings.intervals.form.duration_between" />
      </LabelText>
      <KeyEventsWrapper>
        <StyledFormItem
          name="startKeyEvent"
          shouldUpdate
          data-cy="add-interval.start-key-event"
          rules={[{ required: true, message: requiredMessage }]}
        >
          <Select
            components={{ Option: OptionComp }}
            options={events
              ?.filter((event) => event.fieldId !== intervalData.endKeyEventId)
              ?.map((event) => ({
                label: event?.fieldLabel?.toString() ?? '',
                value: event.fieldId,
              }))}
            isDisabled={isDisabled}
            placeholder={placeholderText}
            onChange={(e) => {
              setIntervalData({ ...intervalData, startKeyEventId: e.value });
            }}
          />
        </StyledFormItem>
        <FormItemWithPadding>
          <FormattedMessage id="and" />
        </FormItemWithPadding>
        <StyledFormItem
          name="endKeyEvent"
          shouldUpdate
          rules={[{ required: true, message: requiredMessage }]}
          data-cy="add-interval.end-key-event"
        >
          <Select
            placeholder={placeholderText}
            components={{ Option: OptionComp }}
            isDisabled={isDisabled}
            options={events
              ?.filter(
                (event) => event.fieldId !== intervalData.startKeyEventId,
              )
              .map((event) => ({
                label: event?.fieldLabel?.toString() ?? '',
                value: event.fieldId,
              }))}
            onChange={(e) => {
              setIntervalData({
                ...intervalData,
                endKeyEventId: e.value,
              });
            }}
          />
        </StyledFormItem>
      </KeyEventsWrapper>
    </Form>
  );
};
